import React, { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, Checkbox, Input, Modal, Select } from 'antd'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import {
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySurfaceCard,
  FoodSafetySubmitErrorBox,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import useIsBlocked from 'src/hooks/useIsBlocked'
import {
  addTrainingEmployee,
  fetchEmployeeTraining,
  fetchStaffTraining,
  fetchFoodSafetyDocumentDownload,
  saveTrainingRecord,
  saveTrainingTopic,
  uploadTrainingEvidence,
} from 'src/utils/foodSafetyApi'
import {
  FOOD_SAFETY_DOCUMENT_ACCEPT,
  validateFoodSafetyDocumentFile,
} from 'src/utils/foodSafetyDocuments'

const emptyRecord = today => ({
  trainingDate: today,
  trainer: '',
  notes: '',
  topics: [],
  refresherDate: '',
  refresherMonths: '',
  changeReason: '',
})
const latest = record => {
  const revisions = record?.revisions || []
  return revisions[revisions.length - 1] || null
}
const asList = value => (Array.isArray(value) ? value : [])
const revisionTopics = revision =>
  asList(revision?.topics).map(topic => ({
    ...topic,
    checklist: asList(topic?.checklist),
    covered: asList(topic?.covered),
  }))
const dateLabel = value =>
  value
    ? new Date(`${value}T12:00:00Z`).toLocaleDateString('en-GB', {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : 'Not configured'
const recordedLabel = value => new Date(value).toLocaleString('en-GB')

const Revision = ({ revision, number, evidence, onOpen }) => (
  <div>
    <p>
      <strong>Training date:</strong> {dateLabel(revision?.trainingDate)} ·{' '}
      <strong>Trainer:</strong> {revision?.trainer}
    </p>
    {revisionTopics(revision).map(topic => (
      <div key={topic.key}>
        <strong>{topic.name}</strong>
        {topic.checklist.length > 0 && (
          <ul>
            {topic.checklist.map(item => (
              <li key={item}>
                {topic.covered.includes(item)
                  ? '✓ Covered: '
                  : 'Not marked covered: '}
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    ))}
    {revision.notes && <Notes>{revision.notes}</Notes>}
    <p>
      <strong>Refresher:</strong> {dateLabel(revision.refresherDate)}
      {revision.refresherMonths
        ? ` (every ${revision.refresherMonths} months)`
        : ''}
    </p>
    <Small>
      Recorded by {revision.recordedByName} ·{' '}
      {recordedLabel(revision.recordedAt)}
    </Small>
    {revision.changeReason && (
      <p>
        <strong>Update reason:</strong> {revision.changeReason}
      </p>
    )}
    <p>
      <strong>Evidence · version {number}</strong>
    </p>
    {evidence.length ? (
      evidence.map(doc => (
        <div key={doc._id}>
          <Button type='link' onClick={() => onOpen(doc)}>
            {doc.title || doc.fileName}
          </Button>
          <Small>
            Uploaded by {doc.uploadedByName || 'Unknown'} ·{' '}
            {recordedLabel(doc.uploadedAt)}
          </Small>
        </div>
      ))
    ) : (
      <Small>No evidence uploaded for this version.</Small>
    )}
  </div>
)

const FoodSafetyTrainingScreen = () => {
  const { venueId } = useParams()
  const blocked = useIsBlocked('food-safety')
  const [data, setData] = useState(null)
  const [selected, setSelected] = useState('')
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState('')
  const [refresh, setRefresh] = useState(0)
  const [query, setQuery] = useState('')
  const [modal, setModal] = useState(null)
  const [draft, setDraft] = useState({})
  const [formError, setFormError] = useState('')
  const [busy, setBusy] = useState(false)
  const writing = useRef(false)
  const [uploading, setUploading] = useState(null)
  const uploadLock = useRef(false)

  useEffect(() => {
    setSelected('')
    setData(null)
    setDetail(null)
    setModal(null)
  }, [venueId])

  useEffect(() => {
    if (blocked || !venueId) return undefined
    let active = true
    setError('')
    fetchStaffTraining(venueId)
      .then(result => {
        if (active) setData(result)
        return null
      })
      .catch(err => {
        if (active) setError(err.message)
      })
    return () => {
      active = false
    }
  }, [venueId, blocked, refresh])

  useEffect(() => {
    setDetail(null)
    if (blocked || !selected || !venueId) return undefined
    let active = true
    fetchEmployeeTraining(venueId, selected)
      .then(result => {
        if (active) setDetail(result)
        return null
      })
      .catch(err => {
        if (active) setError(err.message)
      })
    return () => {
      active = false
    }
  }, [venueId, blocked, selected, refresh])

  const openModal = (kind, value = {}) => {
    setFormError('')
    setModal(kind)
    setDraft(value)
  }
  const patch = values => setDraft(previous => ({ ...previous, ...values }))
  const editRecord = record => {
    const revision = latest(record)
    openModal('record', {
      ...revision,
      recordId: record._id,
      revision: record.revision,
      refresherDate: revision.refresherMonths
        ? ''
        : revision.refresherDate || '',
      refresherMonths: revision.refresherMonths || '',
      changeReason: '',
      topics: revisionTopics(revision).map(topic => ({
        key: topic.key,
        covered: topic.covered.filter(item =>
          asList(data.topics)
            .find(t => t.key === topic.key)
            ?.checklist?.includes(item),
        ),
      })),
    })
  }

  const save = async () => {
    if (writing.current) return
    writing.current = true
    setBusy(true)
    setFormError('')
    try {
      if (modal === 'employee') {
        const result = await addTrainingEmployee(venueId, draft.name)
        setSelected(result.employee.key)
      } else if (modal === 'topic') {
        await saveTrainingTopic(venueId, {
          key: draft.key,
          name: draft.name,
          checklist: (draft.checklist || '')
            .split('\n')
            .map(s => s.trim())
            .filter(Boolean),
        })
      } else {
        await saveTrainingRecord(
          venueId,
          { ...draft, subjectKey: selected },
          draft.recordId,
        )
      }
      setModal(null)
      setRefresh(value => value + 1)
    } catch (err) {
      setFormError(err.message || 'Could not save')
    } finally {
      writing.current = false
      setBusy(false)
    }
  }

  const upload = async (event, record) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || uploadLock.current) return
    const problem = validateFoodSafetyDocumentFile(file)
    if (problem) {
      setError(problem)
      return
    }
    uploadLock.current = true
    setUploading(record._id)
    setError('')
    try {
      await uploadTrainingEvidence(venueId, record._id, record.revision, file)
      setRefresh(value => value + 1)
    } catch (err) {
      setError(err.message)
    } finally {
      uploadLock.current = false
      setUploading(null)
    }
  }

  const openEvidence = async doc => {
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      const result = await fetchFoodSafetyDocumentDownload(venueId, doc._id)
      if (tab) tab.location.href = result.url
      else window.location.assign(result.url)
    } catch (err) {
      if (tab) tab.close()
      setError(err.message)
    }
  }

  if (blocked) return <PermissionBlockScreen />
  return (
    <Wrapper>
      <Link to={`/v/${venueId}/food-safety`}>‹ Today’s overview</Link>
      <FoodSafetyHeroBand>
        <FoodSafetyPageTitle>Staff Training</FoodSafetyPageTitle>
        <BodyText>
          Select an employee to view completed training, refresher dates and
          supporting evidence.
        </BodyText>
        <BodyText>
          Refresher schedules follow your venue’s procedures. Earlier records
          and versions are retained.
        </BodyText>
      </FoodSafetyHeroBand>
      {error && (
        <FoodSafetySubmitErrorBox role='alert'>
          {error}
          <Button onClick={() => setRefresh(n => n + 1)}>Retry</Button>
        </FoodSafetySubmitErrorBox>
      )}
      {!data && !error && <Loading />}
      {data && (
        <>
          <Card>
            <H3>Employees</H3>
            <Input
              aria-label='Find employee'
              placeholder='Find employee'
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <BodyText>
              OMS venue users appear automatically. Employees without an OMS
              login can also be added.
            </BodyText>
            {data.canManage && (
              <Actions>
                <Button onClick={() => openModal('employee', { name: '' })}>
                  Add employee without login
                </Button>
                <Button
                  onClick={() =>
                    openModal('topic', { name: '', checklist: '' })
                  }
                >
                  Add training topic
                </Button>
                <Select
                  aria-label='Configure training topic'
                  placeholder='Configure a topic'
                  value={undefined}
                  style={{ minWidth: 210, maxWidth: '100%' }}
                  onChange={key => {
                    const topic = asList(data.topics).find(t => t.key === key)
                    openModal('topic', {
                      ...topic,
                      checklist: asList(topic?.checklist).join('\n'),
                    })
                  }}
                >
                  {asList(data.topics).map(topic => (
                    <Select.Option key={topic.key} value={topic.key}>
                      {topic.name}
                    </Select.Option>
                  ))}
                </Select>
              </Actions>
            )}
            <StaffList>
              {asList(data.staff)
                .filter(s =>
                  String(s?.name || '')
                    .toLowerCase()
                    .includes(query.toLowerCase()),
                )
                .map(staff => (
                  <li key={staff.key}>
                    <StaffButton
                      aria-pressed={selected === staff.key}
                      onClick={() => {
                        if (!staff.key) return
                        setError('')
                        setSelected(staff.key)
                      }}
                    >
                      <strong>{staff.name || 'Historical employee'}</strong>
                      <span>
                        {staff.status} · {staff.recordCount}{' '}
                        {staff.recordCount === 1 ? 'record' : 'records'}
                        {staff.historical
                          ? ' · Historical employee'
                          : staff.hasLogin
                          ? ' · OMS user'
                          : ' · No login required'}
                      </span>
                    </StaffButton>
                  </li>
                ))}
            </StaffList>
            {!asList(data.staff).length && (
              <BodyText>No employees yet.</BodyText>
            )}
          </Card>
          {selected && !detail && !error && <Loading />}
          {detail && (
            <Card>
              <H3>
                {asList(data.staff).find(staff => staff.key === selected)
                  ?.name ||
                  detail.subject?.name ||
                  'Historical employee'}
              </H3>
              {data.canManage &&
                !asList(data.staff).find(s => s.key === selected)
                  ?.historical && (
                  <Button
                    type='primary'
                    onClick={() => openModal('record', emptyRecord(data.today))}
                  >
                    Record training
                  </Button>
                )}
              {!detail.records.length && (
                <BodyText>No training recorded for this employee.</BodyText>
              )}
              {detail.records.map(record => (
                <Record key={record._id}>
                  <H3>
                    {revisionTopics(latest(record))
                      .map(t => t.name)
                      .join(' · ')}
                  </H3>
                  <p>
                    {record.status} · Version {record.revision || 1}
                  </p>
                  <Revision
                    revision={latest(record)}
                    number={record.revision || 1}
                    evidence={asList(record.evidence).filter(
                      doc => doc.revision === record.revision,
                    )}
                    onOpen={openEvidence}
                  />
                  {data.canManage && (
                    <Actions>
                      {!record.legacy && (
                        <Button onClick={() => editRecord(record)}>
                          Update record
                        </Button>
                      )}
                      <label>
                        Supporting document / certificate
                        <input
                          aria-label={`Upload evidence for ${record._id}`}
                          type='file'
                          accept={FOOD_SAFETY_DOCUMENT_ACCEPT}
                          disabled={Boolean(uploading)}
                          onChange={event => upload(event, record)}
                        />
                      </label>
                      {uploading === record._id && <span>Uploading…</span>}
                    </Actions>
                  )}
                  {record.revisions.length > 1 && (
                    <details>
                      <summary>
                        Previous versions ({record.revisions.length - 1})
                      </summary>
                      {record.revisions.slice(0, -1).map((revision, index) => (
                        <Record key={index}>
                          <strong>Version {index + 1}</strong>
                          <Revision
                            revision={revision}
                            number={index + 1}
                            evidence={record.evidence.filter(
                              doc => doc.revision === index + 1,
                            )}
                            onOpen={openEvidence}
                          />
                        </Record>
                      ))}
                    </details>
                  )}
                </Record>
              ))}
            </Card>
          )}
        </>
      )}
      <Modal
        visible={Boolean(modal)}
        title={
          modal === 'employee'
            ? 'Add employee without login'
            : modal === 'topic'
            ? 'Configure training topic'
            : draft.recordId
            ? 'Update training record'
            : 'Record training'
        }
        onCancel={() => {
          if (!busy) setModal(null)
        }}
        onOk={save}
        confirmLoading={busy}
        okText='Save'
        destroyOnClose
      >
        {formError && (
          <FoodSafetySubmitErrorBox role='alert'>
            {formError}
          </FoodSafetySubmitErrorBox>
        )}
        {modal === 'employee' && (
          <Field>
            Name
            <Input
              aria-label='Employee name'
              maxLength={200}
              value={draft.name}
              onChange={e => patch({ name: e.target.value })}
            />
          </Field>
        )}
        {modal === 'topic' && (
          <>
            <Field>
              Topic name
              <Input
                aria-label='Topic name'
                maxLength={200}
                value={draft.name}
                onChange={e => patch({ name: e.target.value })}
              />
            </Field>
            <Field>
              Checklist items (optional, one per line)
              <Input.TextArea
                aria-label='Topic checklist'
                rows={5}
                value={draft.checklist}
                onChange={e => patch({ checklist: e.target.value })}
              />
            </Field>
            <p>
              Changing this topic does not alter completed training records.
            </p>
          </>
        )}
        {modal === 'record' && (
          <>
            <Field>
              Training date
              <Input
                aria-label='Training date'
                type='date'
                max={data?.today}
                value={draft.trainingDate}
                onChange={e => patch({ trainingDate: e.target.value })}
              />
            </Field>
            <Field>
              Trainer
              <Input
                aria-label='Trainer'
                maxLength={200}
                value={draft.trainer}
                onChange={e => patch({ trainer: e.target.value })}
              />
            </Field>
            <Field>Topics covered</Field>
            {asList(data?.topics).map(topic => {
              const chosen = draft.topics?.find(t => t.key === topic.key)
              return (
                <Topic key={topic.key}>
                  <Checkbox
                    checked={Boolean(chosen)}
                    onChange={e =>
                      patch({
                        topics: e.target.checked
                          ? [...draft.topics, { key: topic.key, covered: [] }]
                          : draft.topics.filter(t => t.key !== topic.key),
                      })
                    }
                  >
                    {topic.name}
                  </Checkbox>
                  {chosen &&
                    asList(topic.checklist).map(item => (
                      <div key={item}>
                        <Checkbox
                          checked={asList(chosen.covered).includes(item)}
                          onChange={e =>
                            patch({
                              topics: draft.topics.map(t =>
                                t.key === topic.key
                                  ? {
                                      ...t,
                                      covered: e.target.checked
                                        ? [...t.covered, item]
                                        : t.covered.filter(s => s !== item),
                                    }
                                  : t,
                              ),
                            })
                          }
                        >
                          {item}
                        </Checkbox>
                      </div>
                    ))}
                </Topic>
              )
            })}
            <Field>
              Notes (optional)
              <Input.TextArea
                aria-label='Training notes'
                rows={3}
                maxLength={5000}
                value={draft.notes}
                onChange={e => patch({ notes: e.target.value })}
              />
            </Field>
            <Field>
              Refresher date (optional)
              <Input
                aria-label='Refresher date'
                type='date'
                min={draft.trainingDate}
                value={draft.refresherDate}
                onChange={e =>
                  patch({ refresherDate: e.target.value, refresherMonths: '' })
                }
              />
            </Field>
            <Field>
              Or interval in months (optional)
              <Input
                aria-label='Refresher interval in months'
                type='number'
                min={1}
                max={120}
                value={draft.refresherMonths}
                onChange={e =>
                  patch({ refresherMonths: e.target.value, refresherDate: '' })
                }
              />
            </Field>
            {draft.recordId && (
              <Field>
                Reason for update
                <Input.TextArea
                  aria-label='Reason for update'
                  maxLength={1000}
                  value={draft.changeReason}
                  onChange={e => patch({ changeReason: e.target.value })}
                />
              </Field>
            )}
            <p>
              Save the record, then attach supporting documents or certificates.
            </p>
            {draft.recordId && (
              <p>
                Updates use the current topic checklist. Previous versions and
                their evidence remain available.
              </p>
            )}
          </>
        )}
      </Modal>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-width: 1060px;
  margin: 2rem auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
const Card = styled(FoodSafetySurfaceCard)`
  padding: 1.25rem;
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0;
  input[type='file'] {
    display: block;
    max-width: 100%;
  }
`
const Field = styled.label`
  display: block;
  margin: 0.8rem 0;
  font-weight: 600;
`
const Small = styled.div`
  font-size: 0.85rem;
  color: #595959;
  overflow-wrap: anywhere;
`
const StaffList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
`
const StaffButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  margin-bottom: 0.4rem;
  text-align: left;
  cursor: pointer;
  overflow-wrap: anywhere;
  span {
    display: block;
  }
  &[aria-pressed='true'] {
    border-color: #7cb305;
    background: #f6ffed;
  }
`
const Record = styled.div`
  border-top: 1px solid #d9d9d9;
  padding: 1rem 0;
  margin-top: 1rem;
  overflow-wrap: anywhere;
  summary {
    cursor: pointer;
  }
`
const Topic = styled.div`
  margin: 0.5rem 0;
  > div {
    margin-left: 1.25rem;
  }
`
const Notes = styled.p`
  white-space: pre-wrap;
`

export default FoodSafetyTrainingScreen
